import React, { useState } from 'react';
import {
  getResourceApi,
  QuickSearch,
  QuickSearchRequestWithCompaniesContext,
} from '@erecruit/modules';
import { Resources } from '@erecruit/types';
import { Option, OptGroup, Highlight, Icon } from '@erecruit/ui-kit';
import { Intl } from '@erecruit/core';
import { useHistory } from 'react-router-dom';

const { FormattedMessage } = Intl;

const generateUniqueValue = (id: number, section: 'positions' | 'matches') => {
  return `${section}#${id}`;
};

const degenerateUniqueValue = (
  value: string,
): { section: string; id: string } => {
  const values = value.split('#');
  return { section: values[0], id: values[1] };
};

const sanitizeTitle = (title: string) =>
  title.replace('<b>', '').replace('</b>', '');

interface SearchComponentProps {
  setOverlayVisible: (visible: boolean) => void;
}

const SearchComponent: React.FC<SearchComponentProps &
  Intl.InjectedIntlProps> = ({ setOverlayVisible }) => {
  const [searchValue, setSearchValue] = useState();
  const history = useHistory();

  const onSelect = (value: string) => {
    const { section, id } = degenerateUniqueValue(value);
    history.push(`/${section}/${id}`);
  };

  return (
    <QuickSearch
      searchPlaceholder={
        <FormattedMessage id={messageIds.vms.quickSearch.placeholder} />
      }
      searchTooShortText={
        <>
          <Icon type="action-error" size="small" />{' '}
          <FormattedMessage id={messageIds.vms.quickSearch.searchTooShort} />
        </>
      }
      noDataText={
        <FormattedMessage id={messageIds.vms.quickSearch.noDataTitle} />
      }
      onSearchValueChange={value => setSearchValue(value)}
      onOverlayVisible={setOverlayVisible}
      onSelect={value => onSelect(value as string)}
      fetchData={async (searchTerm: string): Promise<Resources.QuickSearch> => {
        const data = await getResourceApi().getResources([
          new QuickSearchRequestWithCompaniesContext({
            term: searchTerm,
            count: 20,
          }),
        ]);

        return data[0].data[0];
      }}
      renderOptions={(data: Resources.QuickSearch) => {
        const options = [];

        if (!data.positions.length && !data.candidates.length) {
          options.push(
            <Option
              key="no-data"
              disabled={true}
              style={{
                textAlign: 'center',
                color: 'rgba(34, 65, 123, 0.5)',
                padding: '27px 0',
              }}
            >
              <h3 style={{ color: 'rgba(34, 65, 123, 0.5)' }}>
                <b>
                  <FormattedMessage
                    id={messageIds.vms.quickSearch.noDataTitle}
                  />
                </b>
              </h3>
              <p>
                <FormattedMessage
                  id={messageIds.vms.quickSearch.noDataDescription}
                />
              </p>
            </Option>,
          );
        }

        if (data.positions.length) {
          options.push(
            <OptGroup
              key="positions"
              label={
                <FormattedMessage id={messageIds.vms.positions.positions} />
              }
            >
              {data.positions.map(({ id, title }) => (
                <Option key={id} value={generateUniqueValue(id, 'positions')}>
                  <Highlight
                    text={`${sanitizeTitle(title)} (${id})`}
                    highlight={searchValue}
                  />
                </Option>
              ))}
            </OptGroup>,
          );
        }

        if (data.candidates.length) {
          options.push(
            <OptGroup
              key="candidates"
              label={
                <FormattedMessage id={messageIds.vms.quickSearch.candidates} />
              }
            >
              {data.candidates.map(
                ({ email, firstName, lastName, phone, matches }) =>
                  matches.map(({ id }) => (
                    <Option key={id} value={generateUniqueValue(id, 'matches')}>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '2fr 3fr 90px',
                          gridColumnGap: 12,
                        }}
                      >
                        <div>
                          <Highlight
                            text={`${firstName} ${lastName} (${id})`}
                            highlight={searchValue}
                          />
                        </div>
                        <div>
                          {email && (
                            <Highlight
                              text={email}
                              highlight={searchValue}
                              color="rgba(36, 45, 75, 0.5)"
                            />
                          )}
                        </div>
                        <div>
                          {phone ? (
                            <>
                              <Icon
                                type="call"
                                size="small"
                                style={{ marginRight: '4px' }}
                              />
                              <Highlight text={phone} highlight={searchValue} />
                            </>
                          ) : (
                            '-'
                          )}
                        </div>
                      </div>
                    </Option>
                  )),
              )}
            </OptGroup>,
          );
        }

        return options;
      }}
    />
  );
};

export default Intl.injectIntl(SearchComponent);
